import * as THREE from 'three/webgpu';

import { uNight, uWarm } from '../core/uniforms.js';

/**
 * @file `bakeImpostor` — renders a low-poly `Object3D` into a sprite ATLAS at the
 * exact camera pitch the game plays at, so a distant mesh can be swapped for one
 * textured quad without anybody noticing.
 *
 * Two clients, one function: T8's forest trees (`mesh | billboard | hybrid`
 * backends) and wave D's enemy sprite backend (`types.js -> EnemySpriteBackend`).
 * It is therefore ENGINE code — nothing in here knows what a tree is.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS ALLOWED TO LOOK LIKE A MESH
 * ─────────────────────────────────────────────────────────────────────────────
 * The game camera has a FIXED pitch and its yaw only ever lands on exact
 * multiples of a snap step (`actors/CameraRig.js`). So the set of views a static
 * prop can ever present is FINITE. Bake exactly those views, at exactly that
 * pitch, and the impostor is not an approximation of the mesh — for those yaws it
 * is a pixel-accurate photograph of it.
 *
 * That is why `pitchDeg` defaults to 52 (the `EngineOpts.pitchDeg` default) and
 * why the projection is ORTHOGRAPHIC: a perspective bake would only be correct at
 * the one distance it was baked from, and impostors live at every distance past
 * the LOD radius.
 *
 * ⚠ Pair this with `render/Billboards.js`'s SCREEN-ALIGNED default
 * (`billboardVertical: true`). A ground-rooted card (`vertical: false`) leans away
 * from the camera by `cos(pitch)`, and since the atlas is ALREADY foreshortened by
 * the bake, the impostor would be foreshortened twice.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ATLAS LAYOUT — the one convention, shared with Billboards.js
 * ─────────────────────────────────────────────────────────────────────────────
 * `rows = yaws.length`, `cols = frames`. Frame index is **row-major from the
 * TOP-LEFT**, matching {@link import('./Billboards.js').frameUVRect}. Rows are
 * yaw steps, columns are animation frames. {@link impostorFrameUV} is the CPU
 * mirror used by the tests to prove the two agree.
 *
 * Row selection at runtime is {@link impostorRowFor}: the view a prop presents
 * depends only on `objectYaw - cameraYaw`, so a prop whose yaw is quantised to the
 * same step as the bake is EXACT at every camera snap (see the note there).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * COLOUR / TIME-OF-DAY
 * ─────────────────────────────────────────────────────────────────────────────
 * The bake is a snapshot: whatever `uNight`/`uWarm` say at bake time is burned in.
 * So by default this function forces them to 0 (full day, no gold grade) and
 * restores them afterwards — the atlas holds the DAY channel, and the caller
 * reproduces night with `SpriteBatch.set(..., {tintD, tintN})`, exactly the way
 * `InstancedBatch`'s `iColD`/`iColN` modulate an instanced mesh. One atlas, whole
 * cycle, no second bake.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE THING A FLAT IMPOSTOR CANNOT DO — AND WHAT TO DO ABOUT IT
 * ─────────────────────────────────────────────────────────────────────────────
 * The bake is ORTHOGRAPHIC; the game camera is PERSPECTIVE. A 9 m tree's crown is
 * `9 * sin(52°) ≈ 7 m` CLOSER to the camera than its base, so the mesh renders its
 * crown ~15% larger than its base at a 45 m viewing distance, while a flat quad
 * renders its whole height at ONE depth. Anchor the quad at the object's feet and
 * the crown comes out ~15% too small — visible as a "shrink" the instant you A/B
 * the backends (measured, not theorised: `/play/forest-gallery`).
 *
 * The fix is free: anchor the quad at the object's MID-HEIGHT instead, so the
 * error splits either side of the middle (±8% instead of −15% at the top). Use
 * {@link ImpostorAtlas.liftY} + {@link ImpostorAtlas.anchorYMid} together:
 *
 * ```js
 * new SpriteBatch({ ..., anchorY: atlas.anchorYMid });
 * batch.set(i, x, groundY + atlas.liftY * scale, z, { scale });
 * ```
 *
 * `anchorY` (feet) is still the right choice for anything short — a 1.6 m creature
 * has a 3% error and wants its feet pinned exactly.
 *
 * Render targets are rendered in the renderer's WORKING colour space (linear —
 * `Renderer.currentColorSpace` only returns `outputColorSpace` for the canvas), so
 * the atlas texture is tagged `LinearSRGBColorSpace` and sampling is an identity.
 * Tagging it sRGB would double-decode and crush the darks.
 *
 * @module engine/render/ImpostorBaker
 */

/** @typedef {import('../types.js').EngineCaps} EngineCaps */

/**
 * @typedef {Object} ImpostorAtlas
 * @property {THREE.Texture} texture The atlas — hand it straight to
 *   `new SpriteBatch({ texture, cols, rows })`.
 * @property {number} cols Animation frames per yaw.
 * @property {number} rows Yaw steps.
 * @property {number} worldHeight Metres one FRAME spans vertically on screen —
 *   `SpriteBatch`'s `worldHeight`. NOT the object's height: it is the object's
 *   projected height at `pitchDeg`, margin included.
 * @property {number} worldWidth Metres one frame spans horizontally.
 * @property {number} aspect `worldWidth / worldHeight` — `SpriteBatch`'s `aspect`.
 * @property {number} anchorY Where the object's ORIGIN sits inside the frame, as
 *   a fraction from the frame's bottom. Feed it to `SpriteBatch`'s `anchorY` and
 *   the sprite's feet land on the ground exactly where the mesh's did.
 * @property {number} liftY Object-space Y of the projected content's midpoint.
 * @property {number} anchorYMid `anchorY`'s partner for the MID-HEIGHT anchoring
 *   described in the header — use both or neither.
 * @property {number[]} yaws The baked yaw steps, radians.
 * @property {number} pitchDeg
 * @property {{w: number, h: number}} cell Frame size in pixels.
 * @property {{w: number, h: number}} atlas Atlas size in pixels.
 * @property {number} ms Bake time.
 * @property {THREE.RenderTarget} renderTarget The owner of `texture`.
 * @property {(objectYaw: number, cameraYaw: number) => number} rowFor
 * @property {() => void} dispose Idempotent; frees the render target.
 */

const DEG2RAD = Math.PI / 180;
const TAU = Math.PI * 2;

const _box = new THREE.Box3();
const _prevViewport = new THREE.Vector4();
const _prevScissor = new THREE.Vector4();
const _prevClear = new THREE.Color();

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Renders `object3D` into an impostor atlas.
 *
 * The object is NOT modified: it is temporarily re-parented under a private
 * pivot (so the yaw rotation does not stomp the caller's own transform) and put
 * back before this resolves. Its material must be self-contained — no scene fog
 * (the bake scene has none), no lights (there are none anywhere in this project).
 *
 * @param {THREE.WebGPURenderer} renderer Live, already `init()`ed renderer —
 *   `LevelBuildCtx.renderer` is exactly this.
 * @param {THREE.Object3D} object3D What to photograph. Origin at the ground
 *   contact point (every `build/prims.js` factory already does this).
 * @param {Object} [opts]
 * @param {number} [opts.pitchDeg=52] Camera pitch below horizontal. MUST match
 *   the level's gameplay pitch.
 * @param {number[]} [opts.yaws] Yaw steps, radians; one atlas ROW each. Default
 *   is 4 steps of 90 degrees. 8 steps of 45 are the right answer when the camera
 *   starts on a 45-degree yaw (the `EngineOpts.yawDeg` default) — see
 *   {@link impostorRowFor}.
 * @param {number} [opts.frames=1] Animation frames per yaw; one atlas COLUMN
 *   each. Use with `onFrame` to pose the object.
 * @param {number} [opts.cell=96] Pixel size of a frame's LONGER side. The shorter
 *   side follows the projected aspect, so nothing is wasted on a tall thin prop.
 * @param {number} [opts.margin=0.06] Fraction of transparent padding added around
 *   the silhouette. Non-zero is REQUIRED: it is what keeps a filtered sample at a
 *   frame's edge from bleeding into its neighbour.
 * @param {number} [opts.fitRadius] Overrides the object's XZ radius, so several
 *   variants can share one scale (a forest where every tree has its own metres-
 *   per-pixel reads as a soup). The vertical extent is then computed as a
 *   cylinder of this radius spanning the object's Y range.
 * @param {'nearest'|'linear'} [opts.filter='nearest'] Atlas filtering. MIPMAPS ARE
 *   ALWAYS OFF (an atlas mip level averages across frame boundaries). `'linear'`
 *   is the better choice whenever the impostor is drawn much smaller than `cell`.
 * @param {number} [opts.maxTextureSize=2048] Hard cap; the cell is scaled down to
 *   fit. Pass `caps.maxTextureSize`.
 * @param {number} [opts.samples=0] MSAA on the offscreen target. 0 is the
 *   portable default; 4 softens silhouettes at the cost of an alpha-test bite.
 * @param {boolean} [opts.neutralizeTime=true] Force `uNight`/`uWarm` to 0 for the
 *   bake (see the header) and restore afterwards.
 * @param {((frame: number, t: number, object: THREE.Object3D) => void)|null} [opts.onFrame]
 *   Called before each COLUMN with `t = frame / max(1, frames - 1)`; pose the
 *   object here. Wave D's creature walk cycles hang off this.
 * @param {string} [opts.name='impostor'] Debug name on the texture/target.
 * @returns {Promise<ImpostorAtlas>}
 */
export async function bakeImpostor(renderer, object3D, opts = {}) {
  const {
    pitchDeg = 52,
    yaws = [0, Math.PI / 2, Math.PI, Math.PI * 1.5],
    frames = 1,
    cell = 96,
    margin = 0.06,
    fitRadius = null,
    filter = 'nearest',
    maxTextureSize = 2048,
    samples = 0,
    neutralizeTime = true,
    onFrame = null,
    name = 'impostor',
  } = opts;

  if (!renderer) throw new Error('bakeImpostor: a renderer is required');
  if (!object3D) throw new Error('bakeImpostor: an object3D is required');
  if (!Array.isArray(yaws) || yaws.length === 0) throw new Error('bakeImpostor: `yaws` must be a non-empty array');

  const t0 = now();
  // Harmless when already up: `init()` memoises its own promise.
  if (renderer.initialized === false) await renderer.init();

  const rows = yaws.length;
  const cols = Math.max(1, frames | 0);
  const pitch = pitchDeg * DEG2RAD;

  // ── projection ─────────────────────────────────────────────────────────────
  const view = projectExtents(object3D, yaws, pitch, margin, fitRadius);
  const worldWidth = view.maxX - view.minX;
  const worldHeight = view.maxY - view.minY;
  if (!(worldWidth > 0) || !(worldHeight > 0)) {
    throw new Error('bakeImpostor: object has no projected extent (empty geometry?)');
  }

  const px = frameSize(worldWidth / worldHeight, cell, cols, rows, maxTextureSize);
  const atlasW = px.w * cols;
  const atlasH = px.h * rows;

  // ── target ─────────────────────────────────────────────────────────────────
  const rt = new THREE.RenderTarget(atlasW, atlasH, {
    format: THREE.RGBAFormat,
    type: THREE.UnsignedByteType,
    depthBuffer: true,
    stencilBuffer: false,
    samples,
  });
  const tex = rt.texture;
  tex.name = `${name}.atlas`;
  const f = filter === 'linear' ? THREE.LinearFilter : THREE.NearestFilter;
  tex.magFilter = f;
  tex.minFilter = f; // never *MipmapLinear: there are no mips, by design
  tex.generateMipmaps = false;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  // Render targets are written in the WORKING colour space (see the header).
  tex.colorSpace = THREE.LinearSRGBColorSpace;

  // ── scene + camera ─────────────────────────────────────────────────────────
  const scene = new THREE.Scene();
  scene.background = null;
  scene.fog = null; // the atlas must never bake fog in — distance is a runtime fact

  const pivot = new THREE.Group();
  pivot.name = `${name}.pivot`;
  const prevParent = object3D.parent;
  pivot.add(object3D);
  scene.add(pivot);

  // Push the camera clear of the deepest corner, then give `far` the same slack.
  const dist = Math.max(1, view.maxDepth) + 2;
  const depthSpan = view.maxDepth - view.minDepth + 4;
  const cam = new THREE.OrthographicCamera(view.minX, view.maxX, view.maxY, view.minY, 0.01, dist + depthSpan);
  // Camera basis at yaw 0: X = (1,0,0), Y = (0,cos p,-sin p), Z = (0,sin p,cos p),
  // which is exactly `Engine.updateCamera()` with `yaw = 0`.
  cam.position.set(0, Math.sin(pitch) * dist, Math.cos(pitch) * dist);
  cam.up.set(0, 1, 0);
  cam.lookAt(0, 0, 0);
  cam.updateMatrixWorld();

  // ── renderer state (every line of this is restored below) ──────────────────
  const prevTarget = renderer.getRenderTarget();
  const prevAutoClear = renderer.autoClear;
  const prevScissorTest = renderer.getScissorTest();
  const prevAlpha = renderer.getClearAlpha();
  renderer.getClearColor(_prevClear);
  renderer.getViewport(_prevViewport);
  renderer.getScissor(_prevScissor);
  const prevNight = uNight.value;
  const prevWarm = uWarm.value;

  try {
    if (neutralizeTime) {
      uNight.value = 0;
      uWarm.value = 0;
    }

    renderer.setRenderTarget(rt);
    renderer.setScissorTest(false);
    renderer.setClearColor(0x000000, 0);
    // One full clear, then LOAD for every cell — a per-cell clear would wipe the
    // frames already in the atlas (clear ops are per-attachment, not per-viewport,
    // on both backends).
    renderer.clear(true, true, false);
    renderer.autoClear = false;

    for (let c = 0; c < cols; c++) {
      if (onFrame) onFrame(c, cols > 1 ? c / (cols - 1) : 0, object3D);
      for (let r = 0; r < rows; r++) {
        pivot.rotation.y = yaws[r];
        pivot.updateMatrixWorld(true);
        const vp = impostorFrameViewport(cols, rows, r * cols + c, px.w, px.h);
        rt.viewport.set(vp.x, vp.y, vp.w, vp.h);
        renderer.render(scene, cam);
      }
    }
  } finally {
    renderer.autoClear = prevAutoClear;
    renderer.setRenderTarget(prevTarget);
    renderer.setScissorTest(prevScissorTest);
    renderer.setClearColor(_prevClear, prevAlpha);
    renderer.setViewport(_prevViewport.x, _prevViewport.y, _prevViewport.z, _prevViewport.w);
    renderer.setScissor(_prevScissor.x, _prevScissor.y, _prevScissor.z, _prevScissor.w);
    rt.viewport.set(0, 0, atlasW, atlasH);
    if (neutralizeTime) {
      uNight.value = prevNight;
      uWarm.value = prevWarm;
    }
    // Hand the object back exactly as we found it.
    pivot.rotation.y = 0;
    pivot.remove(object3D);
    if (prevParent) prevParent.add(object3D);
    scene.remove(pivot);
  }

  let disposed = false;
  const cosPitch = Math.cos(pitch);

  return {
    texture: tex,
    cols,
    rows,
    worldHeight,
    worldWidth,
    aspect: worldWidth / worldHeight,
    anchorY: clamp01((0 - view.minY) / worldHeight),
    liftY: view.liftY,
    anchorYMid: clamp01((view.liftY * cosPitch - view.minY) / worldHeight),
    yaws: yaws.slice(),
    pitchDeg,
    cell: { w: px.w, h: px.h },
    atlas: { w: atlasW, h: atlasH },
    ms: now() - t0,
    renderTarget: rt,
    rowFor: (objectYaw, cameraYaw) => impostorRowFor(yaws, objectYaw, cameraYaw),
    dispose() {
      if (disposed) return;
      disposed = true;
      rt.dispose();
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout maths (pure — the tests pin these against Billboards.js)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pixel rect of one frame, in the RENDER TARGET's coordinate system (origin
 * bottom-left, the GL convention three uses for viewports on both backends).
 *
 * Frame 0 is the TOP-LEFT cell, so row 0 sits at the TOP of the target, i.e. the
 * HIGHEST y. That inversion is the whole reason this helper exists.
 *
 * @param {number} cols @param {number} rows @param {number} frame
 * @param {number} cellW @param {number} cellH
 * @returns {{x: number, y: number, w: number, h: number}}
 */
export function impostorFrameViewport(cols, rows, frame, cellW, cellH) {
  const total = Math.max(1, cols * rows);
  const f = ((Math.floor(frame) % total) + total) % total;
  const col = f % cols;
  const row = Math.floor(f / cols);
  return {
    x: col * cellW,
    y: (rows - 1 - row) * cellH,
    w: cellW,
    h: cellH,
  };
}

/**
 * The UV rect a frame occupies, DERIVED FROM {@link impostorFrameViewport} rather
 * than restated — which is what makes `tests/forest.test.mjs`'s comparison against
 * `Billboards.frameUVRect` meaningful: it proves the pixels the baker WROTE are
 * the pixels the shader READS.
 *
 * @param {number} cols @param {number} rows @param {number} frame
 * @returns {{u0: number, v0: number, u1: number, v1: number}}
 */
export function impostorFrameUV(cols, rows, frame) {
  const vp = impostorFrameViewport(cols, rows, frame, 1, 1);
  return { u0: vp.x / cols, v0: vp.y / rows, u1: (vp.x + 1) / cols, v1: (vp.y + 1) / rows };
}

/**
 * Which atlas row shows `objectYaw` seen from `cameraYaw`.
 *
 * Rotating the world by `-cameraYaw` puts the camera back at the bake's +Z and
 * leaves the object at `objectYaw - cameraYaw`; pitch survives a Y rotation
 * untouched. So the view depends ONLY on that difference — and if the caller
 * quantises `objectYaw` to the same step the atlas was baked at, every camera
 * snap lands on an EXACT row and the impostor is pixel-correct rather than
 * nearest-neighbour correct.
 *
 * @param {number[]} yaws Baked steps, radians.
 * @param {number} objectYaw
 * @param {number} cameraYaw
 * @returns {number} Row index.
 */
export function impostorRowFor(yaws, objectYaw, cameraYaw) {
  const want = wrapTau(objectYaw - cameraYaw);
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < yaws.length; i++) {
    const d = Math.abs(angleDelta(wrapTau(yaws[i]), want));
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * `n` evenly spaced yaw steps starting at `offset` — the argument `bakeImpostor`
 * wants for anything but the 4-step default.
 * @param {number} n
 * @param {number} [offset=0]
 * @returns {number[]}
 */
export function yawSteps(n, offset = 0) {
  const out = [];
  const k = Math.max(1, n | 0);
  for (let i = 0; i < k; i++) out.push(offset + (i * TAU) / k);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// internals
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Screen-space (camera-space XY) AABB of the object over EVERY yaw, plus the
 * depth span. One projection has to serve all frames or `worldHeight` would mean
 * something different per row.
 *
 * @param {THREE.Object3D} object3D
 * @param {number[]} yaws
 * @param {number} pitch Radians.
 * @param {number} margin
 * @param {number|null} fitRadius
 * @returns {{minX:number,maxX:number,minY:number,maxY:number,minDepth:number,maxDepth:number,liftY:number}}
 */
function projectExtents(object3D, yaws, pitch, margin, fitRadius) {
  object3D.updateMatrixWorld(true);
  _box.setFromObject(object3D, true);
  if (_box.isEmpty()) throw new Error('bakeImpostor: object3D has no geometry to measure');

  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minD = Infinity;
  let maxD = -Infinity;

  const bx = [_box.min.x, _box.max.x];
  const by = [_box.min.y, _box.max.y];
  const bz = [_box.min.z, _box.max.z];

  if (fitRadius != null && fitRadius > 0) {
    // A cylinder of `fitRadius` spanning the object's Y range: yaw-invariant by
    // construction, which is exactly what "share one scale" means.
    minX = -fitRadius;
    maxX = fitRadius;
    minY = by[0] * cp - fitRadius * sp;
    maxY = by[1] * cp + fitRadius * sp;
    minD = by[0] * sp - fitRadius * cp;
    maxD = by[1] * sp + fitRadius * cp;
  } else {
    for (const yaw of yaws) {
      const c = Math.cos(yaw);
      const s = Math.sin(yaw);
      for (let i = 0; i < 8; i++) {
        const x = bx[i & 1];
        const y = by[(i >> 1) & 1];
        const z = bz[(i >> 2) & 1];
        // object.rotation.y = yaw
        const rx = x * c + z * s;
        const rz = -x * s + z * c;
        // camX=(1,0,0)  camY=(0,cos p,-sin p)  camZ=(0,sin p,cos p)
        const sx = rx;
        const sy = y * cp - rz * sp;
        const sd = y * sp + rz * cp;
        if (sx < minX) minX = sx;
        if (sx > maxX) maxX = sx;
        if (sy < minY) minY = sy;
        if (sy > maxY) maxY = sy;
        if (sd < minD) minD = sd;
        if (sd > maxD) maxD = sd;
      }
    }
  }

  const padX = (maxX - minX) * margin * 0.5;
  const padY = (maxY - minY) * margin * 0.5;
  return {
    minX: minX - padX,
    maxX: maxX + padX,
    minY: minY - padY,
    maxY: maxY + padY,
    minDepth: minD,
    maxDepth: maxD,
    // Object-space Y of the content's middle — the depth a mid-anchored quad
    // should sit at. See the perspective note in the header.
    liftY: (by[0] + by[1]) * 0.5,
  };
}

/**
 * Frame size in pixels: the longer side gets `cell`, the shorter follows the
 * projected aspect, and the whole atlas is scaled down if it would blow the
 * texture cap.
 * @param {number} aspect w/h
 * @param {number} cell
 * @param {number} cols @param {number} rows
 * @param {number} maxTextureSize
 * @returns {{w: number, h: number}}
 */
function frameSize(aspect, cell, cols, rows, maxTextureSize) {
  const base = Math.max(4, Math.round(cell));
  let w;
  let h;
  if (aspect >= 1) {
    w = base;
    h = Math.max(4, Math.round(base / aspect));
  } else {
    h = base;
    w = Math.max(4, Math.round(base * aspect));
  }
  const cap = Math.max(64, maxTextureSize || 2048);
  const shrink = Math.min(1, cap / (w * cols), cap / (h * rows));
  if (shrink < 1) {
    w = Math.max(4, Math.floor(w * shrink));
    h = Math.max(4, Math.floor(h * shrink));
  }
  return { w, h };
}

/** @param {number} a @returns {number} `a` wrapped into [0, 2pi). */
function wrapTau(a) {
  return ((a % TAU) + TAU) % TAU;
}

/** @param {number} a @param {number} b @returns {number} Shortest signed a->b. */
function angleDelta(a, b) {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return d;
}

/** @param {number} v */
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
